import React, { useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./LoginScreen.css";
import "../../../src/style.css";
import SalonService from "../../services/SalonService";
import { useNavigate } from "react-router-dom";
import LoadingIndicator from '../loadingIndicator/LoadingIndicator';
import { useSalon } from "../../context/SalonContext";

const LoginScreen = () => {
    const { login } = useSalon();
    const [customerName, setCustomerName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [timer, setTimer] = useState(30);
    const [showResend, setShowResend] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    //const [searchParams] = useSearchParams();
    const timeout = setTimeout(() => { setLoading(false); }, 30000);

    useEffect(() => {
        let interval;
        if (otpSent && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (otpSent && timer === 0) {
            setShowResend(true);
        }
        return () => clearInterval(interval);
    }, [otpSent, timer]);

    const sendOtp = async () => {
        if (!customerName.trim()) {
            setErrorMessage("Please enter your name");
            return;
        }

        const cleanNumber = normalizeNumber(phoneNumber);

        // ✅ Must be exactly 10 digits
        if (cleanNumber.length !== 10) {
            setErrorMessage("Please enter a valid 10-digit mobile number");
            return;
        }

        setErrorMessage("");
        setLoading(true);

        const data = { mobileNumber: "+91" + cleanNumber };
        try {
            const response = await SalonService.sendLoginOTP(data);
            clearTimeout(timeout);
            if (response.status === 200) {
                setOtpSent(true);
                setTimer(30);
                setShowResend(false);
            }
        } catch (e) {
            clearTimeout(timeout);
            setErrorMessage(e.response?.data || "Failed to send OTP");
        } finally {
            setLoading(false);
        }
    };

    const resendOtp = () => {
        sendOtp();
        setTimer(30);
        setShowResend(false);
    };

    const verifyOtp = async () => {
        if (otp.length !== 6) {
            setErrorMessage("Invalid OTP");
            return;
        }

        const cleanNumber = normalizeNumber(phoneNumber);

        // ✅ re-check validity
        if (cleanNumber.length !== 10) {
            setErrorMessage("Invalid mobile number");
            return;
        }

        setLoading(true);
        setErrorMessage("");
        const data = {
            mobileNumber: "+91" + cleanNumber,
            otp,
            name: customerName
        };

        try {
            const response = await SalonService.customerLogin(data);
            clearTimeout(timeout);
            if (response.status === 200) {
                login(
                    customerName,
                    response.data.mobileNumber,
                    response.data.accessToken,
                    response.data.customerId,
                    response.data.refreshToken
                );

                // ✅ Always redirect to booking (salonUuid comes from context/localStorage)
                navigate("/booking", { replace: true });
            }
        } catch (e) {
            clearTimeout(timeout);
            setErrorMessage(e.response?.data?.message || "Login failed");
            localStorage.removeItem("ACCESS_TOKEN");
        } finally {
            setLoading(false);
        }
    };

    const normalizeNumber = (num) => {
        if (!num) return "";
        let clean = num.replace(/\D/g, "");   // keep only digits

        // remove leading "91" if react-phone-input-2 already includes it
        if (clean.startsWith("91") && clean.length > 10) {
            clean = clean.slice(2);
        }

        clean = clean.replace(/^0+/, "");     // strip leading zeros (like 097.. → 97..)
        return clean;
    };

    return (
        <div className="login-container">
            <div className="card login-card">
                <h2 className="login-title">
                    {!otpSent ? "Welcome 👋" : "Verification Code Sent 📩"}
                </h2>
                <p className="login-subtitle">
                    {!otpSent
                        ? "Enter your details to book an appointment"
                        : `Please enter the code sent to +91 ${phoneNumber.slice(2)}`}
                </p>


                {loading && <LoadingIndicator />}
                {!otpSent ? (
                    <>
                        <input
                            type="text"
                            className="input"
                            placeholder="Enter Your Name"
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                        />

                        <PhoneInput
                            country="in"                 // default India
                            onlyCountries={["in"]}       // restrict to India only
                            disableDropdown={true}       // remove dropdown
                            countryCodeEditable={false}  // lock +91, cannot be edited
                            value={phoneNumber}
                            onChange={setPhoneNumber}
                            placeholder="Enter Mobile Number"
                            inputClass="input"
                            buttonClass="phone-flag"     // optional custom styling
                        />

                        <button className="btn-primary full-width" onClick={sendOtp}>
                            Send OTP
                        </button>
                    </>
                ) : (
                    <>
                        <input
                            className="input"
                            type="text"
                            placeholder="Enter OTP"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                        />
                        <p className="countdown-text">Resend available in: {timer}s</p>
                        {showResend && (
                            <div className="resend-otp" onClick={resendOtp}>
                                Resend OTP
                            </div>
                        )}
                        <button className="btn-primary full-width" onClick={verifyOtp}>
                            Verify OTP
                        </button>
                    </>
                )}
                {errorMessage && <p className="error">{errorMessage}</p>}
            </div>
        </div>
    );
};

export default LoginScreen;
